import { forwardRef, useImperativeHandle } from "react";
import { BlockNoteView } from "@blocknote/mantine";
import {
  BasicTextStyleButton,
  BlockTypeSelect,
  ColorStyleButton,
  FileCaptionButton,
  FileReplaceButton,
  FormattingToolbar,
  FormattingToolbarController,
  TextAlignButton,
  useCreateBlockNote,
} from "@blocknote/react";
import styled from "@emotion/styled";
import * as ReactPDF from "@react-pdf/renderer";

import { AI_REPORT_CONFIG, useVariableValue } from "lib/featureFlags/generated/dvcVariableTypes";
import { downloadFile, generateFilename } from "../../../lib/utils";
import {
  createStotlesCalloutBlock,
  StotlesCalloutBlockProps,
} from "./custom_blocks/stotlesCalloutBlock";
import AddImageButton from "./custom_buttons/AddImageButton";
import CreateLinkButton from "./custom_buttons/CreateLinkButton";
import RedoButton from "./custom_buttons/RedoButton";
import ReportItemsDropdownButton from "./custom_buttons/ReportItemsDropdownButton";
import UndoButton from "./custom_buttons/UndoButton";
import WordCountButton from "./custom_buttons/WordCountButton";
import { getBlockNoteEditorStyles } from "./BlockNoteEditor.styles";
import { StotlesBlockNoteEditor, stotlesBlockNoteSchema, StotlesPartialBlock } from "./types";
import {
  ContentPosition,
  ExportHeaderContent,
  getCharacterCount,
  getDocxBlob,
  getPdfDocument,
  getWordCount,
  insertContent,
  isBlockNoteEmpty,
} from "./utils";

import "@blocknote/mantine/style.css";

const DEFAULT_PARAGRAPH_PLACEHOLDER = "Start writing or use / to get a set of tools...";
/**
 * Props for the BlockNoteEditor component
 * @property initialContent - Initial content for the editor as an array of partial blocks
 * @property onChange - Callback function triggered when the editor content changes
 * @property editable - Whether the editor is in read-only mode (default: true)
 * @property minHeight - Minimum height of the editor (default: "400px")
 * @property sideMenu - Whether to show the side menu for block manipulation (default: false)
 * @property theme - Theme variant for the editor (default: "light")
 * @property staticFormattingToolbar - Whether to show a static formatting toolbar above the editor (default: false)
 * @property placeholder - Placeholder text for the main paragraph block (overrides the default)
 * @property uploadFile - Function to handle file uploads, should return a promise that resolves to the uploaded file URL
 */
export interface BlockNoteEditorProps {
  initialContent?: StotlesPartialBlock[];
  onChange?: (content: StotlesPartialBlock[]) => void;
  editable?: boolean;
  minHeight?: string | number;
  sideMenu?: boolean;
  theme?: "light" | "dark";
  staticFormattingToolbar?: boolean;
  placeholder?: string;
  uploadFile?: (file: File) => Promise<string>;
}

/**
 * Ref interface for accessing editor methods
 * @property getEditor - Get the current BlockNote editor instance
 * @property getContent - Get the current content as an array of partial blocks
 * @property clearContent - Clear all content from the editor
 * @property isEmpty - Check if the editor is empty
 * @property getWordCount - Get the word count of the editor content
 * @property getCharacterCount - Get the character count of the editor content
 */
export interface BlockNoteEditorRef {
  getEditor: () => StotlesBlockNoteEditor | null;
  getContent: () => StotlesPartialBlock[];
  clearContent: () => void;
  isEmpty: () => boolean;
  isFocused: () => boolean;
  getWordCount: () => number;
  getCharacterCount: () => number;
  exportToDocx: (fileName?: string, headerContent?: ExportHeaderContent) => Promise<void>;
  exportToPdf: (fileName?: string, headerContent?: ExportHeaderContent) => Promise<void>;
  insertContent: (content: string, position?: ContentPosition) => void;
  insertStotlesCallout: (props: StotlesCalloutBlockProps) => void;
}

/**
 * BlockNoteEditor component - A rich text editor built on top of BlockNote
 *
 * BlockNote is a modern, block-based rich text editor that provides an intuitive
 * editing experience similar to Notion. It supports various block types including
 * headings, paragraphs, lists, and more.
 *
 * Features:
 * - Block-based editing with drag and drop support
 * - Rich text formatting (bold, italic, underline, etc.)
 * - Multiple block types (headings, lists, etc.)
 * - Light and dark theme support
 * - Read-only mode
 * - Customizable minimum height
 * - Optional side menu for block manipulation
 * - Ref support for programmatic control
 *
 * @example
 * ```tsx
 * // Basic usage
 * <BlockNoteEditor
 *   onChange={(content) => console.log('Content:', content)}
 * />
 *
 * // With initial content
 * <BlockNoteEditor
 *   initialContent={[
 *     { type: "heading", props: { level: 1 }, content: "Welcome" },
 *     { type: "paragraph", content: "Start writing..." }
 *   ]}
 *   theme="light"
 *   minHeight="500px"
 * />
 *
 * // With ref for programmatic control
 * const editorRef = useRef<BlockNoteEditorRef>(null);
 *
 * const handleSave = () => {
 *   const content = editorRef.current?.getContent();
 *   console.log('Saving:', content);
 * };
 *
 * <BlockNoteEditor ref={editorRef} />
 * ```
 *
 * Keyboard Shortcuts:
 * - Bold: Cmd/Ctrl + B
 * - Italic: Cmd/Ctrl + I
 * - Underline: Cmd/Ctrl + U
 * - Strikethrough: Cmd/Ctrl + Shift + S
 * - Undo: Cmd/Ctrl + Z
 * - Redo: Cmd/Ctrl + Shift + Z
 * - New block: Enter
 * - Convert to heading: Type # at start of line
 * - Convert to list: Type - or * at start of line
 *
 * @param props - The editor component props
 * @param ref - Ref for accessing editor methods
 */
export const BlockNoteEditor = forwardRef<BlockNoteEditorRef, BlockNoteEditorProps>(
  (
    {
      initialContent,
      onChange,
      editable = true,
      minHeight = "400px",
      sideMenu = false,
      theme = "light",
      staticFormattingToolbar = false,
      placeholder = DEFAULT_PARAGRAPH_PLACEHOLDER,
      uploadFile,
    },
    ref,
  ) => {
    const editor = useCreateBlockNote({
      schema: stotlesBlockNoteSchema,
      initialContent,
      placeholders: {
        default: "",
      },
      uploadFile,
    });

    const handleChange = () => {
      if (onChange) {
        onChange(editor.document);
      }
    };

    // Using useImperativeHandle to expose the editor instance and content methods
    useImperativeHandle(ref, () => ({
      getEditor: () => editor,
      getContent: () => editor.document,
      clearContent: () => {
        editor.replaceBlocks(editor.document, []);
      },
      isEmpty: () => isBlockNoteEmpty(editor.document),
      isFocused: () => editor.isFocused(),
      getWordCount: () => getWordCount(editor.document),
      getCharacterCount: () => getCharacterCount(editor.document),
      exportToDocx: async (fileName?: string, headerContent?: ExportHeaderContent) => {
        const blob = await getDocxBlob(editor, headerContent);
        const filename = generateFilename(fileName, "docx");
        downloadFile(blob, filename);
      },
      exportToPdf: async (fileName?: string, headerContent?: ExportHeaderContent) => {
        const pdfDocument = await getPdfDocument(editor, headerContent);
        const filename = generateFilename(fileName, "pdf");
        const blob = await ReactPDF.pdf(pdfDocument).toBlob();
        downloadFile(blob, filename);
      },
      insertContent: async (content: string, position?: ContentPosition) => {
        await insertContent(editor, content, position, handleChange);
      },
      insertStotlesCallout: (props: StotlesCalloutBlockProps) => {
        const currentBlock = editor.getTextCursorPosition().block;
        editor.insertBlocks([createStotlesCalloutBlock(props)], currentBlock, "after");
        handleChange();
      },
    }));

    const isAiReportConfigEnabled = useVariableValue(AI_REPORT_CONFIG, false);

    return (
      <EditorWrapper>
        {isBlockNoteEmpty(editor.document) && (
          <PlaceholderOverlay sideMenu={sideMenu} staticFormattingToolbar={staticFormattingToolbar}>
            {placeholder}
          </PlaceholderOverlay>
        )}
        <EditorContainer minHeight={minHeight} sideMenu={sideMenu}>
          <BlockNoteView
            editor={editor}
            theme={theme}
            sideMenu={sideMenu}
            editable={editable}
            formattingToolbar={!staticFormattingToolbar}
            onChange={handleChange}
            // We need this to be true to use the theme variables
            data-theming-css-variables-enabled="true"
          >
            {staticFormattingToolbar && (
              // Static toolbar which is at the top of the editor (not sticky so not always visible)
              <FormattingToolbar>
                <UndoButton key="undoButton" />
                <RedoButton key="redoButton" />
                <ToolbarDivider />
                <BlockTypeSelect key="blockTypeSelect" />
                <FileCaptionButton key="fileCaptionButton" />
                <FileReplaceButton key="replaceFileButton" />

                <BasicTextStyleButton basicTextStyle="bold" key="boldStyleButton" />
                <BasicTextStyleButton basicTextStyle="italic" key="italicStyleButton" />
                <BasicTextStyleButton basicTextStyle="underline" key="underlineStyleButton" />

                <TextAlignButton textAlignment="left" key="textAlignLeftButton" />
                <TextAlignButton textAlignment="center" key="textAlignCenterButton" />
                <TextAlignButton textAlignment="right" key="textAlignRightButton" />

                <ColorStyleButton key="colorStyleButton" />
                <AddImageButton key="addImageButton" />
                <CreateLinkButton key="createLinkButton" />
                {isAiReportConfigEnabled && (
                  <>
                    <ToolbarDivider />
                    <ReportItemsDropdownButton key="reportItemsDropdownButton" />
                    <ToolbarDivider />
                  </>
                )}
                <WordCountButton key="wordCountButton" />
              </FormattingToolbar>
            )}
            {staticFormattingToolbar && (
              // Floating toolbar which shows on text selection only
              <FormattingToolbarController
                formattingToolbar={() => (
                  <FormattingToolbar>
                    <BlockTypeSelect key="blockTypeSelect" />
                    <FileCaptionButton key="fileCaptionButton" />
                    <FileReplaceButton key="replaceFileButton" />

                    <BasicTextStyleButton basicTextStyle="bold" key="boldStyleButton" />
                    <BasicTextStyleButton basicTextStyle="italic" key="italicStyleButton" />
                    <BasicTextStyleButton basicTextStyle="underline" key="underlineStyleButton" />

                    <TextAlignButton textAlignment="left" key="textAlignLeftButton" />
                    <TextAlignButton textAlignment="center" key="textAlignCenterButton" />
                    <TextAlignButton textAlignment="right" key="textAlignRightButton" />

                    <ColorStyleButton key="colorStyleButton" />
                    <CreateLinkButton key="createLinkButton" />
                  </FormattingToolbar>
                )}
              />
            )}
          </BlockNoteView>
        </EditorContainer>
      </EditorWrapper>
    );
  },
);

const EditorContainer = styled.div<{ minHeight?: string | number; sideMenu: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;

  .bn-container {
    min-height: ${({ minHeight }) =>
      minHeight ? (typeof minHeight === "number" ? `${minHeight}px` : minHeight) : "auto"};
  }

  ${({ theme, sideMenu }) => getBlockNoteEditorStyles(theme, sideMenu)}
`;

const EditorWrapper = styled.div({
  position: "relative",
  width: "100%",
});

const PlaceholderOverlay = styled.div<{ sideMenu: boolean; staticFormattingToolbar: boolean }>(
  ({ sideMenu, staticFormattingToolbar, theme }) => ({
    position: "absolute",
    top: staticFormattingToolbar ? 62 : 2,
    left: sideMenu ? 24 : 0,
    pointerEvents: "none",
    color: theme.colors.sysTextPlaceholder,
    fontSize: "14px",
    zIndex: 1,
  }),
);

const ToolbarDivider = styled.div(({ theme }) => ({
  width: "1px",
  backgroundColor: theme.colors.sysBorderDecorative,
  margin: "4px 8px",
}));

BlockNoteEditor.displayName = "BlockNoteEditor";
